import { useGetInsurancesQuery } from 'services/insurance/insurance';
import { myAccountApi, useLazyGetMyAccountQuery } from 'services/myAccount/myAccount';

import { selectUser } from 'store';

import { VerificationBannerKeysProps } from 'widgets/VerificationBanner/verificationBanner.types';

import {
  WM_INSURANCE_VALID_PRICE_POINTS,
  WM_NEW_VALID_ONBOARDING_PRICE_POINTS,
  WM_VALID_ONBOARDING_PRICE_POINTS_FOR_PARTNER_FLOWS
} from 'constants/pricepoints';
import { useAppSelector } from 'hooks';
import { useMyPrograms } from 'hooks/useMyPrograms/useMyPrograms';
import { usePartnerPatient } from 'hooks/usePartnerPatient';
import { useRouteMatch } from 'hooks/useRouteMatch';
import { PathName } from 'router/paths';

import { ProgramCategory } from 'models/programs.types';

/**
 * Program categories whose intake treats a pharmacy as optional, so the
 * global completeness check must not demand one.
 *
 * Men's Health's intake offers the step but lets the patient skip it
 * (`PO_PHARMACY_STEP_SKIPPED`, with completion tied to reaching `mif-summary`
 * rather than to pharmacy selection), and the dynamic TRT POMIF omits the
 * step entirely (`TRT_PROGRAM_CONFIG.includePharmacy`). Requiring one anyway
 * pinned the red "Finish health profile" banner on forever for a patient who
 * answered every question they were shown — and because that banner and
 * "Verify your ID" are mutually exclusive in `banner.settings.ts`, it also
 * masked an outstanding ID (LMD-1051).
 */
const PHARMACY_OPTIONAL_CATEGORIES: ProgramCategory[] = ['mens-health'];

/**
 * Categories that ride along without owning an intake of their own, so they
 * neither impose the pharmacy requirement nor grant the exemption — they are
 * skipped before the decision is made.
 *
 * Needed because a TRT patient also carries an active LifeMD+ membership under
 * `category: 'default'` (`isIncluded: true` — bundled with the plan, never
 * separately chosen). Counting it as a voter meant the exemption silently
 * failed for every real Men's Health patient. Skipping these rather than
 * folding them into the optional list above keeps membership-only and
 * freemium patients on the original requirement, since they have no
 * pharmacy-optional intake to justify an exemption.
 */
const PHARMACY_NEUTRAL_CATEGORIES: ProgramCategory[] = ['default'];

export const useCompleteProfile = (program: 'any' | ProgramCategory = 'any') => {
  const shouldSkipInsuranceQuery =
    program === 'women-health' || program === 'mental-health' || program === 'mens-health';
  const {
    data,
    isFetching,
    isLoading,
    isUninitialized,
    refetch: refetchInsurance
  } = useGetInsurancesQuery(undefined, {
    skip: shouldSkipInsuranceQuery
  });
  const isBlueLinePatient = usePartnerPatient('Blue Line');
  const isBalladHealthPatient = usePartnerPatient('Ballad Health');
  const {
    activePricePoints,
    activePrograms,
    mensHealth,
    mentalHealth,
    preferredMedications,
    weightManagement,
    womensHealth
  } = useMyPrograms();
  const isZoomCall = useRouteMatch(PathName.ZoomCall)?.isExact;
  const [getMyAccount, { isFetching: isFetchingMyAccount, isLoading: isLoadingMyAccount }] =
    useLazyGetMyAccountQuery();
  const {
    address,
    bodyImage,
    identityVerified,
    isVerifiedByVouched,
    pharmacy,
    useNewPostOnboarding,
    userId
  } = useAppSelector(selectUser);

  const didNotAnsweredRegardingInsurance = data?.data.hasInsurance === null;
  const isGettingMyAccountExternal =
    myAccountApi.endpoints.getMyAccount.useQueryState(undefined)?.isLoading;
  // preferredMedication is required only with the Weight Management program for the completeness check
  const preferredMedication = preferredMedications.find(
    (pm) => pm.program.category === 'weight-management'
  )?.preferredMedication;

  const shouldAddInsuranceForSpecificFlow = useNewPostOnboarding
    ? !!preferredMedications?.length &&
      preferredMedication?.includes('ins') &&
      didNotAnsweredRegardingInsurance
    : true;

  // Prevent phantom data by ensuring we have legitimate user data before showing incomplete profile banner
  const hasValidUserData = !!userId && (shouldSkipInsuranceQuery || !isUninitialized);
  // Only programs that own an intake get a say. Of those, every one must treat
  // pharmacy as optional: someone on Men's Health AND Weight Management still
  // needs a pharmacy for the WM side. No deciding programs at all (bundled
  // memberships only, or none) keeps the original requirement.
  const pharmacyDecidingPrograms = activePrograms.filter(
    (p) => !!p?.program?.category && !PHARMACY_NEUTRAL_CATEGORIES.includes(p.program.category)
  );
  const isPharmacyOptional =
    pharmacyDecidingPrograms.length > 0 &&
    pharmacyDecidingPrograms.every((p) =>
      PHARMACY_OPTIONAL_CATEGORIES.includes(p.program.category)
    );
  const isCurrentlyLoading =
    isFetching ||
    isLoading ||
    isFetchingMyAccount ||
    isLoadingMyAccount ||
    isGettingMyAccountExternal;

  // Only return filtered keys if we have valid user data and we're not currently loading
  // This prevents phantom banners from appearing during data transitions
  const getUncompletedPOSteps = (): { [key in VerificationBannerKeysProps[number]]?: boolean } => {
    if (program === 'any') {
      // false if something is not completed
      return {
        address: !!address,
        bodyImage:
          bodyImage?.uploadRequired !== true ? true : bodyImage?.manualStatus === 'accepted',
        insurance: useNewPostOnboarding
          ? !shouldAddInsuranceForSpecificFlow
          : !didNotAnsweredRegardingInsurance ||
            !weightManagement ||
            isBlueLinePatient ||
            isBalladHealthPatient,
        isVerifiedPerson: !(!isVerifiedByVouched && !identityVerified && isZoomCall),
        miCompletionStatus: activePrograms.every((p) => p?.details?.miCompletionStatus !== false),
        // Pharmacy is required for every patient — clients want a local pharmacy on file
        // regardless of program/medication (per LMD-26812) — EXCEPT for programs whose
        // intake never obliges the patient to pick one. See
        // `PHARMACY_OPTIONAL_CATEGORIES`.
        pharmacy: isPharmacyOptional || !!pharmacy?.pharmacyId,
        selectedPreferredMedication: !(
          activePricePoints.some((price) =>
            [
              ...WM_NEW_VALID_ONBOARDING_PRICE_POINTS,
              ...WM_VALID_ONBOARDING_PRICE_POINTS_FOR_PARTNER_FLOWS,
              ...WM_INSURANCE_VALID_PRICE_POINTS
            ].includes(price)
          ) && preferredMedication === null
        )
      };
    }
    const config: Record<
      ProgramCategory,
      { [key in VerificationBannerKeysProps[number]]?: boolean }
    > = {
      default: {
        address: !!address,
        miCompletionStatus: true
      },
      'mens-health': {
        isVerifiedPerson: !(!isVerifiedByVouched && !identityVerified && isZoomCall),
        miCompletionStatus: !!mensHealth?.details?.miCompletionStatus,
        // Deliberately NOT exempted like the `'any'` branch above: `Banner`'s
        // `geltLink()` keeps pointing an incomplete Men's Health patient at
        // `IntakeFormMensTRT` for as long as these keys are non-empty, so
        // dropping pharmacy here would change where the banner links, not
        // just whether it shows.
        pharmacy: !!pharmacy?.pharmacyId
      },
      'mental-health': {
        address: !!address,
        bodyImage:
          bodyImage?.uploadRequired !== true ? true : bodyImage?.manualStatus === 'accepted',
        isVerifiedPerson: !(!isVerifiedByVouched && !identityVerified && isZoomCall),
        miCompletionStatus: mentalHealth?.details?.miCompletionStatus
      },
      'weight-management': {
        address: !!address,
        bodyImage:
          bodyImage?.uploadRequired !== true ? true : bodyImage?.manualStatus === 'accepted',
        insurance: useNewPostOnboarding
          ? !shouldAddInsuranceForSpecificFlow
          : !didNotAnsweredRegardingInsurance || isBlueLinePatient || isBalladHealthPatient,
        isVerifiedPerson: !(!isVerifiedByVouched && !identityVerified && isZoomCall),
        miCompletionStatus: !!weightManagement?.details?.miCompletionStatus,
        pharmacy: !!pharmacy?.pharmacyId,
        selectedPreferredMedication: !(
          activePricePoints.some((price) =>
            [
              ...WM_NEW_VALID_ONBOARDING_PRICE_POINTS,
              ...WM_VALID_ONBOARDING_PRICE_POINTS_FOR_PARTNER_FLOWS,
              ...WM_INSURANCE_VALID_PRICE_POINTS
            ].includes(price)
          ) && preferredMedication === null
        )
      },
      'women-health': {
        isVerifiedPerson: !(!isVerifiedByVouched && !identityVerified && isZoomCall),
        miCompletionStatus: !!womensHealth?.details?.miCompletionStatus,
        pharmacy: !!pharmacy?.pharmacyId
      }
    };
    return config[program] ?? {};
  };

  const filteredKeys: VerificationBannerKeysProps = Object.entries(getUncompletedPOSteps())
    .filter(([, value]) => !value)
    .map(([key]) => key) as VerificationBannerKeysProps;

  const refresh = async () => {
    await getMyAccount().unwrap();
    if (!isUninitialized) {
      await refetchInsurance().unwrap();
    }
  };

  const stableFilteredKeys = hasValidUserData && !isCurrentlyLoading ? filteredKeys : [];
  const isBodyImageRejected =
    hasValidUserData && !isCurrentlyLoading && bodyImage?.manualStatus === 'invalid';

  return {
    filteredKeys: stableFilteredKeys,
    isBodyImageRejected,
    isFetching: isFetching || isFetchingMyAccount,
    isLoading: isLoading || isLoadingMyAccount,
    // temporary flag for Men's Health onboarding flow, mirrors isWHNotCompleted
    isMensHealthNotCompleted: !!mensHealth && !mensHealth.details?.miCompletionStatus,
    // An empty `filteredKeys` is ambiguous until the first load lands; refetches don't reset this.
    isReady: hasValidUserData && !isLoading && !isLoadingMyAccount && !isGettingMyAccountExternal,
    // temporary flag for WH onboarding flow, should be rewritten later to work with every program
    isWHNotCompleted: !!womensHealth && !womensHealth.details?.miCompletionStatus,
    refresh
  };
};
